import { clearAllFormCaches, localStorageStore } from './form-cache';

export type Session = { token: string | null; householdId: string | null };

export interface SecureBackend {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

export class AuthStoreNotReadyError extends Error {}

export interface AuthStore {
  init(): Promise<void>;
  getSession(): Session;
  setSession(token: string, householdId: string): Promise<void>;
  clearToken(): Promise<void>;
  clearAll(): Promise<void>;
}

export const TOKEN_KEY = 'bk_token';
export const HOUSEHOLD_KEY = 'bk_household_id';

export function createAuthStore(
  opts: { native: false; ls: Storage } | { native: true; ls: Storage; secure: SecureBackend },
): AuthStore {
  const { ls } = opts;
  let ready = false;
  let memory: Session = { token: null, householdId: null };

  function assertReady(): void {
    if (!ready) throw new AuthStoreNotReadyError('authStore.init() not called');
  }

  return {
    init() {
      if (!opts.native) {
        ready = true;
        return Promise.resolve();
      }
      const { secure } = opts;
      return (async () => {
        const token = ls.getItem(TOKEN_KEY);
        if (token) {
          await secure.set(TOKEN_KEY, token);
          ls.removeItem(TOKEN_KEY);
        }
        const householdId = ls.getItem(HOUSEHOLD_KEY);
        if (householdId) {
          await secure.set(HOUSEHOLD_KEY, householdId);
          ls.removeItem(HOUSEHOLD_KEY);
        }
        memory = {
          token: await secure.get(TOKEN_KEY),
          householdId: await secure.get(HOUSEHOLD_KEY),
        };
        ready = true;
      })();
    },

    getSession() {
      assertReady();
      if (opts.native) return { ...memory };
      const token = ls.getItem(TOKEN_KEY);
      const householdId = ls.getItem(HOUSEHOLD_KEY);
      return { token: token || null, householdId: householdId || null };
    },

    async setSession(token, householdId) {
      assertReady();
      if (!opts.native) {
        const previousToken = ls.getItem(TOKEN_KEY);
        try {
          ls.setItem(TOKEN_KEY, token);
          ls.setItem(HOUSEHOLD_KEY, householdId);
        } catch (error) {
          try {
            if (ls.getItem(TOKEN_KEY) === token && previousToken !== token) {
              ls.removeItem(TOKEN_KEY);
            }
          } catch {}
          throw error;
        }
        return;
      }
      const previous = { ...memory };
      memory = { token, householdId };
      try {
        await opts.secure.set(TOKEN_KEY, token);
        await opts.secure.set(HOUSEHOLD_KEY, householdId);
      } catch (error) {
        memory = previous;
        try {
          if (previous.token !== null) await opts.secure.set(TOKEN_KEY, previous.token);
          else await opts.secure.remove(TOKEN_KEY);
          if (previous.householdId !== null) await opts.secure.set(HOUSEHOLD_KEY, previous.householdId);
          else await opts.secure.remove(HOUSEHOLD_KEY);
        } catch {
          for (const key of [TOKEN_KEY, HOUSEHOLD_KEY]) {
            try {
              await opts.secure.remove(key);
            } catch {}
          }
          memory = { token: null, householdId: null };
        }
        throw error;
      }
    },

    async clearToken() {
      assertReady();
      if (!opts.native) {
        ls.removeItem(TOKEN_KEY);
        return;
      }
      memory.token = null;
      await opts.secure.remove(TOKEN_KEY);
    },

    async clearAll() {
      assertReady();
      if (opts.native) {
        memory = { token: null, householdId: null };
        await opts.secure.remove(TOKEN_KEY);
        await opts.secure.remove(HOUSEHOLD_KEY);
      } else {
        ls.removeItem(TOKEN_KEY);
        ls.removeItem(HOUSEHOLD_KEY);
      }
      ls.removeItem('bk_currency');
      ls.removeItem('bk_last_category');
      clearAllFormCaches(localStorageStore(ls));
    },
  };
}
